import React, {Dispatch, SetStateAction, useEffect, useState} from 'react';
import {Field, Form, Formik} from 'formik';
import {ValuesRequestTeamType} from '../../types/TypesPage';
import {useDebounce} from 'use-debounce';
import RowCheckboxInPopupFilter from './Components/RowCheckboxInPopupFilter';
import RowDateFilter from './Components/RowDateFilter';
import RangeSliderFilter from './RangeSlider/RangeSliderFilter';
import CheckBoxSelector from '../../PersonalOffice/MyTeam/Components/CheckBoxSelector';
import GreenButton from '../Buttons/GreenButton';
import {useTranslation} from 'react-i18next';
import LightGreenButton from '../Buttons/LightGreenButton';
import FilterToggleButton from '../Buttons/FilterToggleButton';
import FilterSearchButton from '../Buttons/FilterSearchButton';
import {monthNow} from '../../assets/utils/dateUtils';
import {userQualifications} from '../../assets/utils/translatersFunc';
import FilterMonth from './Components/FilterMonth';
import styles from './MyTeamFilterNew.module.css';

type Props = {
	setFilters: Dispatch<SetStateAction<ValuesRequestTeamType>>;
};

const MyTeamFilterNew: React.FC<Props> = ({setFilters}) => {
	const {t} = useTranslation();
	const date = new Date();
	const year = date.getFullYear();
	const fullMonthName = date.toLocaleString('uk-UA', {month: 'long'});
	const [filterMenu, setFilterMenu] = useState(false);
	const [currentMonthName, setCurrentMonthName] = useState<string | null>(
		`${fullMonthName} ${year}`,
	);
	const [amountOfMonth, setAmountOfMonth] = useState<number>(0);
	const [search, setSearch] = useState<string>('');
	const [debounceSearch] = useDebounce(search, 500);

	const levels = Array.from({length: 6}, (_, i) => ({
		name: (i + 1).toString(),
		value: (i + 1).toString(),
	}));
	const handleChangeSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
		setSearch(e.currentTarget.value.trim());
	};
	const handleSubmit = (values: ValuesRequestTeamType) => {
		const transformedValues = {
			...values,
			qlfcnames: Array.isArray(values.qlfcnames)
				? values.qlfcnames.join(',')
				: values.qlfcnames,
			// levels теперь число, а не массив
			levels: Array.isArray(values.levels)
				? values.levels[0] // берем первое значение из массива
				: typeof values.levels === 'number'
				? values.levels
				: values.levels,
		};
		const filteredValues = Object.fromEntries(
			Object.entries(transformedValues).filter(
				([_, value]) => value && value !== '' && value !== null,
			),
		);

		setFilters(filteredValues);
		setSearch('');
		setFilterMenu(false);
	};
	useEffect(() => {
		const trimmed = debounceSearch.trim();

		if (trimmed.length === 0) {
			setFilters((prev) => {
				if (!prev?.search) {
					return prev;
				}

				const {search: _search, ...rest} = prev;
				const keys = Object.keys(rest);

				if (keys.length === 0) {
					return {non_filter: true};
				}

				return {
					...rest,
				};
			});
		} else if (trimmed.length >= 3) {
			setFilters((prev) => ({
				...prev,
				search: trimmed,
				non_filter: false,
			}));
		}
	}, [debounceSearch, setFilters]);

	const initialValues: ValuesRequestTeamType = {
		month: monthNow(),
		non_filter: false,
		active_points_from: null,
		active_points_tree_from: null,
		active_points_to: null,
		active_points_tree_to: null,
		levels: '',
		qlfcnames: '',
		manager_id_tree: null,
		manager_id: '',
		out_manager_id: '',
		old_tree: false,
		manager_type: false,
		optima_type: false,
		old_contract: false,
		not_active_contract: false,
		loyal_type: false,
		not_active: false,
		registration_date: '',
		state: '',
		city: '',
	};
	return (
		<Formik
			enableReinitialize
			initialValues={initialValues}
			onSubmit={handleSubmit}
		>
			{({values, handleSubmit, setFieldValue, resetForm}) => {
				return (
					<Form onSubmit={handleSubmit}>
						<div className={styles.header}>
							<FilterToggleButton
								isActive={filterMenu}
								onClick={() => setFilterMenu(!filterMenu)}
								variant="green"
								activeText={t('закрити')}
								inactiveText={t('додати фільтр')}
							/>
							<FilterSearchButton
								value={search}
								onChange={handleChangeSearch}
								placeholder={t('Пошук партнера')}
								onKeyDown={(e) => {
									if (e.key === 'Enter') {
										e.preventDefault();
									}
								}}
							/>
						</div>
						<div
							className={`${styles.filterItem} ${!filterMenu ? '' : styles.filterItemActive}`}
						>
							<div className={styles.wrapper}>
								<div className={styles.form}>
									<FilterMonth
										currentMonthName={currentMonthName || ''}
										setCurrentMonthName={setCurrentMonthName}
										setAmountOfMonth={setAmountOfMonth}
										setCurrentMonth={(month) => setFieldValue('month', month)}
									/>

									<RangeSliderFilter
										title={t('Особистий ТО')}
										valueRange={[
											values.active_points_from
												? +values.active_points_from
												: 0,
											values.active_points_to ? +values.active_points_to : 99,
										]}
										setFieldValue={setFieldValue}
										nameFieldFrom={'active_points_from'}
										nameFieldTo={'active_points_to'}
									/>
									<RangeSliderFilter
										title={t('Командний ТО')}
										valueRange={[
											values.active_points_tree_from
												? +values.active_points_tree_from
												: 0,
											values.active_points_tree_to
												? +values.active_points_tree_to
												: 100000,
										]}
										setFieldValue={setFieldValue}
										nameFieldFrom={'active_points_tree_from'}
										nameFieldTo={'active_points_tree_to'}
									/>
									<CheckBoxSelector
										title={t('Лінія')}
										optionsData={levels}
										fieldName={'levels'}
										values={values}
										nameOption={t('Лінія')}
									/>

									<CheckBoxSelector
										title={t('Кваліфікація')}
										optionsData={userQualifications}
										fieldName={'qlfcnames'}
										values={values}
										nameOption={''}
									/>

									<div className={`${styles.formItem} ${styles.formItemId1}`}>
										<label htmlFor="showTeam" className={styles.label1}>
											{t('Показати партнера')}
										</label>
										<Field
											id="showTeam"
											placeholder={`№${t('контракту')}`}
											type="number"
											min={'1'}
											step={'1'}
											name="manager_id"
											className={styles.input1}
										/>
									</div>
									<div className={`${styles.formItem} ${styles.formItemId1}`}>
										<label htmlFor="quitStructure" className={styles.label1}>
											{t('Виключити партнера')}
										</label>
										<Field
											id="quitStructure"
											placeholder={`№${t('контракту')}`}
											type="number"
											min={'1'}
											step={'1'}
											name="out_manager_id"
											className={styles.input1}
										/>
									</div>
									<RowCheckboxInPopupFilter
										text={t('Партнери у кваліфікації')}
										checked={values?.manager_type ?? false}
										name={'manager_type'}
									/>
									<RowCheckboxInPopupFilter
										text={t('Тільки Optima')}
										checked={values?.optima_type ?? false}
										name={'optima_type'}
									/>

									<RowCheckboxInPopupFilter
										text={t('Втрачають контракт')}
										checked={values.old_contract ?? false}
										name={'old_contract'}
									/>
									<RowCheckboxInPopupFilter
										text={t('Не активовані контракти')}
										checked={values.not_active_contract ?? false}
										name={'not_active_contract'}
									/>
									<RowDateFilter
										value={values.registration_date}
										name={'registration_date'}
										setFieldValue={setFieldValue}
										text={t('Дата Реєстрації')}
									/>

									<div className={`${styles.formItem} ${styles.formItemId1}`}>
										<label htmlFor="showCountry" className={styles.label1}>
											{t('Країна')}
										</label>
											<Field
												id="showCountry"
												placeholder={t('Україна')}
												type="text"
												name="state"
												className={styles.input1}
											/>
									</div>
									<div className={`${styles.formItem} ${styles.formItemId1}`}>
										<label htmlFor="showCity" className={styles.label1}>
											{t('Місто')}
										</label>
											<Field
												id="showCity"
												placeholder={t('Харків')}
												type="text"
												name="city"
												className={styles.input1}
											/>
									</div>
									<div className={styles.buttons}>
										<GreenButton
											text={t('Очистити')}
											type={'button'}
											onClick={resetForm}
										/>
										<LightGreenButton text={t('Застосувати')} type={'submit'} />
									</div>
								</div>
							</div>
						</div>
					</Form>
				);
			}}
		</Formik>
	);
};

export default MyTeamFilterNew;
